import React, { useContext, useEffect, useRef, useState } from "react"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergCheckbox, MobergColumn, MobergDropdown, MobergIconSize, MobergInputLabel, MobergRow, MobergShortTextInput, MobergText, MobergTheme } from "../../../../../Moberg"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { DeltaRegion, DeltaRegionJSON, HeatmapTraceConfig, LineTraceConfig, RenderStrategy, TraceConfigJSON } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { MdClose } from "react-icons/md"
import { TraceColorPicker } from "./TraceColorPicker"
import { ColorRGB } from "../../../../../Pages/Data/Visualize/DataReview/Types/Color"

export const DeltaModalities = () => {
    const { currentGraph, activeDeltaRegions, reasonAddMeasurementIsDisabled, updateGraphProperty, removeTrace } = useContext(ConfigureWindowModalContext)
    const [colorPickerDeltaRegionId, setColorPickerDeltaRegionId] = useState<string>()
	const colorPickerBlockRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
	const colorPickerRef = useRef<HTMLDivElement | null>(null)

    function addColorPickerBlockRef(node: HTMLDivElement | null, traceId: string) {
		colorPickerBlockRefs.current.set(traceId, node)
	}

    const updateDeltaRegion = (id: string, updatedDeltaRegion: DeltaRegion) => {
		const newTraces = [...(currentGraph?.traces ?? [])].map(trace => {
			if (trace.id === id) {
				return updatedDeltaRegion
			}

			return trace
		})

		updateGraphProperty("traces", newTraces)
	}

    const addDelta = () => {
		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => {
			const firstTrace = currentGraph?.traces.find(trace => trace.renderStrategy !== RenderStrategy.DELTA)

			if (!firstTrace) {
				return previous
			}

			const newTrace: DeltaRegionJSON = {
				id: `trace-${new Date(Date.now()).toISOString()}`,
				renderStrategy: RenderStrategy.DELTA,
				first: firstTrace,
				second: firstTrace,
				name: firstTrace.name,
				color: { r: 0, g: 0, b: 0 },
				isVisible: true
			} as DeltaRegionJSON

			return [
				...(previous ?? []),
				newTrace
			]
		})
	}

    const handleColorChange = (traceId: string, color: ColorRGB) => {
		const newTraces = [...(currentGraph?.traces ?? [])].map(trace => {
			if (trace.id === traceId) {
				return {
					...trace,
					color
				}
			}
			return trace
		})

		updateGraphProperty("traces", newTraces)
	}


	function toggleColorPicker(event: any, deltaRegionId: string) {
		const node = colorPickerRef.current

		if (node?.style.display === "block" && deltaRegionId === colorPickerDeltaRegionId) {
			node.style.display = "none"
			return
		}

		setColorPickerDeltaRegionId(deltaRegionId)

		if (node) {
			node.style.display = "block"
			node.style.top = `${event.target.getBoundingClientRect().bottom}px`
			node.style.left = `${event.target.getBoundingClientRect().right}px`
		}
	}

	useEffect(() => {
		const clickHandler = (event: any) => {
			let clickedOutside = true

			colorPickerBlockRefs.current.forEach(node => {
				if (node?.contains(event.target)) {
					clickedOutside = false
				}
			})

			if (colorPickerRef.current?.contains(event.target)) {
				clickedOutside = false
			}

			if (clickedOutside && colorPickerRef.current) {
				colorPickerRef.current.style.display = "none"
			}
		}

		document.addEventListener("click", clickHandler)
		return () => document.removeEventListener("click", clickHandler)
	})

	const availableTraces = [...(currentGraph?.traces ?? [])].filter(trace => trace.renderStrategy !== RenderStrategy.DELTA) as Array<LineTraceConfig | HeatmapTraceConfig>

	if (availableTraces.length < 2) {
		return (
			<MobergText>
				There need to be at least 2 measurements configured before a delta region can be drawn.
			</MobergText>
		)
	}

    return (
        <MobergColumn gap="8px" style={{ padding: "0 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto auto auto auto auto 1fr", columnGap: "16px", rowGap: "12px", alignItems: "center" }}>
				<MobergInputLabel text={"First"} />
				<MobergInputLabel text={"Second"} />
				<MobergInputLabel text={"Label"} />
				<MobergInputLabel text={"Color"} />
				<MobergInputLabel text={"Visible"} />
				<MobergInputLabel text={""} />

                {activeDeltaRegions.map(deltaRegion => {
					return (
                        <>
                            <MobergDropdown
								options={availableTraces.map(trace => ({ label: trace.name, value: trace }))}
								onChange={traceConfig => updateDeltaRegion(deltaRegion.id, { ...deltaRegion, first: traceConfig })}
								selectedValue={deltaRegion.first}
								equals={(a, b) => a.id === b.id}
								width={250}
							/>

							<MobergDropdown
								options={availableTraces.map(trace => ({ label: trace.name, value: trace }))}
								onChange={traceConfig => updateDeltaRegion(deltaRegion.id, { ...deltaRegion, second: traceConfig })}
								selectedValue={deltaRegion.second}
								equals={(a, b) => a.id === b.id}
								width={250}
							/>

							<MobergShortTextInput 
								limit={25} 
								value={deltaRegion.name}
								onChange={value => updateDeltaRegion(deltaRegion.id, {...deltaRegion, name: value })}/>

                            <MobergRow>
								<div
									onClick={event => toggleColorPicker(event, deltaRegion.id)}
									style={{
										width: "25px",
										height: "25px",
										backgroundColor: deltaRegion.color ? `rgb(${deltaRegion.color.r}, ${deltaRegion.color.g}, ${deltaRegion.color.b})` : "#000",
										border: "1px solid var(--color-border-default)",
										borderRadius: "25px",
										cursor: "pointer",
									}}
									ref={ref => addColorPickerBlockRef(ref, deltaRegion.id)}
								/>
							</MobergRow>

							<MobergCheckbox 
								isChecked={deltaRegion.isVisible ?? true}
								onChange={isVisible => updateDeltaRegion(deltaRegion.id, { ...deltaRegion, isVisible })}
							/>

							<MobergButton shape={MobergButtonShape.SQUARE} onClick={() => removeTrace(deltaRegion.id)} style={{ padding: "6px" }}>
								<MdClose size={MobergIconSize.REGULAR} />
							</MobergButton>
                        </>
                    )})
                }

				<MobergButton onClick={addDelta} theme={MobergTheme.BLUE} variant={MobergButtonVariant.OUTLINE} style={{ width: "250px" }}
					disabled={!!reasonAddMeasurementIsDisabled}
					tooltip={reasonAddMeasurementIsDisabled}>
					Add delta region
				</MobergButton>
            </div>

            <div id={`DeltaColorPickerWindow`} ref={colorPickerRef} style={{ position: "fixed", display: "none", zIndex: 999 }}>
				<TraceColorPicker
					selectedColor={(() => {
						const c = activeDeltaRegions.find(deltaRegion => deltaRegion.id === colorPickerDeltaRegionId)?.color
						return c ? `rgb(${c.r}, ${c.g}, ${c.b})` : "#000"
					})()}
					onChange={hex => {
						if (colorPickerDeltaRegionId !== undefined) {
							const r = parseInt(hex.slice(1, 3), 16)
							const g = parseInt(hex.slice(3, 5), 16)
							const b = parseInt(hex.slice(5, 7), 16)
							handleColorChange(colorPickerDeltaRegionId, { r, g, b })
						}
					}}
					onClose={() => {
						if (colorPickerRef.current) colorPickerRef.current.style.display = "none"
					}}
				/>
			</div>
        </MobergColumn>
    )
}
